import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box, Flex } from "theme-ui";

import UserContext from "context/User";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { mnt } from "theme/maintenanceTokens";

import {
  getLocationNotes,
  createNote,
  deleteNote,
  setNoteClientVisibility,
} from "api/Notes";

/**
 * N1 Release 1 - Location notes with client-portal visibility.
 *
 * Replaces the single `location.siteNotes` textarea with a proper
 * per-note list. Visibility rules (enforced server-side; this component
 * only renders what the endpoint returns + exposes the staff controls):
 *   - A CLIENT sees notes their own org authored + internal notes staff
 *     explicitly marked client-visible. They can add notes (always
 *     visible to internal staff).
 *   - INTERNAL STAFF see every note at the site, can add notes (with a
 *     "visible to client" choice), can toggle any note's client
 *     visibility, and can delete their own notes.
 *
 * The component reads the viewer's role from UserContext to decide which
 * controls to show; the backend is the real gate.
 */

interface LocationNote {
  id: string;
  note: string;
  clientVisible?: boolean;
  createdAt?: string;
  createdBy?: {
    id?: string;
    data?: { firstName?: string; lastName?: string } | null;
    email?: string;
  } | null;
}

interface LocationNotesProps {
  locationId: string;
}

function authorName(n: LocationNote): string {
  const d = n.createdBy?.data;
  const full = [d?.firstName, d?.lastName].filter(Boolean).join(" ").trim();
  return full || n.createdBy?.email || "Unknown";
}

function relativeDate(iso?: string): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Date.now() - then;
  const day = 24 * 60 * 60 * 1000;
  if (diff < day) return "today";
  if (diff < 2 * day) return "yesterday";
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function LocationNotes({ locationId }: LocationNotesProps) {
  const { user } = useContext<any>(UserContext);
  const toast = useToast();

  const isStaff = user?.accessLevel === "isCompany";
  const myUserId = user?.id;

  const [notes, setNotes] = useState<LocationNote[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [newVisible, setNewVisible] = useState(false); // staff: share with client
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const rows = await getLocationNotes({ data: { id: locationId } });
    setNotes(Array.isArray(rows) ? rows : []);
    setLoading(false);
  }, [locationId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async () => {
    const body = draft.trim();
    if (!body) return;
    setSaving(true);
    const res: any = await createNote({
      data: {
        note: body,
        entityType: "Location",
        entityId: locationId,
        // Only staff choose visibility; a client's own note is always
        // visible to staff, and clientVisible is irrelevant to their view.
        ...(isStaff ? { clientVisible: newVisible } : {}),
      },
    });
    setSaving(false);
    if (res?.id) {
      setDraft("");
      setNewVisible(false);
      toast.add("Note added.", "green");
      load();
    } else {
      toast.add("Could not add the note.", "red");
    }
  };

  const handleToggleVisibility = async (n: LocationNote) => {
    setBusyId(n.id);
    const res: any = await setNoteClientVisibility({
      data: { id: n.id },
      visible: !n.clientVisible,
    });
    setBusyId(null);
    if (res?.id) {
      setNotes((prev) =>
        (prev || []).map((x) =>
          x.id === n.id ? { ...x, clientVisible: !!res.clientVisible } : x,
        ),
      );
    } else {
      toast.add("Could not update visibility.", "red");
    }
  };

  const handleDelete = async (n: LocationNote) => {
    setBusyId(n.id);
    const res: any = await deleteNote({ data: { id: n.id } });
    setBusyId(null);
    if (res?.success) {
      setNotes((prev) => (prev || []).filter((x) => x.id !== n.id));
    } else {
      toast.add("Could not delete the note.", "red");
    }
  };

  const rows = notes || [];

  return (
    <Box>
      {/* Composer */}
      <Box sx={{ marginBottom: rows.length ? "16px" : 0 }}>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={
            isStaff
              ? "Add a note about this site (gate codes, access, staging). Internal by default."
              : "Add a note about this site."
          }
          rows={3}
          style={{
            width: "100%",
            fontFamily: "Poppins, sans-serif",
            fontSize: "13px",
            padding: "10px 12px",
            border: `1px solid ${mnt.divider}`,
            borderRadius: "8px",
            resize: "vertical",
            boxSizing: "border-box",
          }}
        />
        <Flex
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: "8px",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          {isStaff ? (
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontFamily: "Poppins, sans-serif",
                fontSize: "12px",
                color: mnt.textMuted,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={newVisible}
                onChange={(e) => setNewVisible(e.target.checked)}
              />
              Visible to the client
            </label>
          ) : (
            <span />
          )}
          <BasicButton
            onClick={handleAdd}
            disabled={saving || !draft.trim()}
            scheme="primary"
          >
            {saving ? "Adding..." : "Add note"}
          </BasicButton>
        </Flex>
      </Box>

      {/* List */}
      {loading ? (
        <Box
          sx={{
            fontFamily: "Poppins",
            fontSize: "13px",
            color: mnt.textMuted,
            padding: "8px 0",
          }}
        >
          Loading notes...
        </Box>
      ) : rows.length === 0 ? (
        <Box
          sx={{
            fontFamily: "Poppins",
            fontSize: "13px",
            color: mnt.textMuted,
            padding: "4px 0",
          }}
        >
          No notes yet.
        </Box>
      ) : (
        <Box>
          {rows.map((n) => {
            const mine = !!myUserId && n.createdBy?.id === myUserId;
            return (
              <Box
                key={n.id}
                sx={{
                  padding: "12px 0",
                  borderTop: `1px solid ${mnt.divider}`,
                  "&:first-of-type": { borderTop: "none" },
                }}
              >
                <Flex
                  sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "4px",
                    gap: "10px",
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: mnt.ink,
                    }}
                  >
                    {authorName(n)}
                    <span
                      style={{
                        color: mnt.textFaint,
                        fontWeight: 400,
                        marginLeft: "8px",
                      }}
                    >
                      {relativeDate(n.createdAt)}
                    </span>
                  </Box>

                  {/* Staff-only: visibility badge + toggle. */}
                  {isStaff && (
                    <Flex sx={{ alignItems: "center", gap: "10px" }}>
                      <Box
                        sx={{
                          fontFamily: "Poppins",
                          fontSize: "11px",
                          fontWeight: 600,
                          padding: "2px 8px",
                          borderRadius: "10px",
                          color: n.clientVisible ? "#2f6f3e" : mnt.textMuted,
                          background: n.clientVisible ? "#e6f4ea" : mnt.bgSubtle,
                        }}
                      >
                        {n.clientVisible ? "Visible to client" : "Internal"}
                      </Box>
                      <Box
                        as="button"
                        onClick={() => handleToggleVisibility(n)}
                        {...({ disabled: busyId === n.id } as any)}
                        sx={{
                          background: "transparent",
                          border: "none",
                          padding: 0,
                          fontFamily: "Poppins",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: mnt.link,
                          cursor: "pointer",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {n.clientVisible ? "Make internal" : "Share with client"}
                      </Box>
                      {mine && (
                        <Box
                          as="button"
                          onClick={() => handleDelete(n)}
                          {...({ disabled: busyId === n.id } as any)}
                          sx={{
                            background: "transparent",
                            border: "none",
                            padding: 0,
                            fontFamily: "Poppins",
                            fontSize: "12px",
                            fontWeight: 600,
                            color: mnt.textMuted,
                            cursor: "pointer",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          Delete
                        </Box>
                      )}
                    </Flex>
                  )}

                  {/* Client: can delete their own notes. */}
                  {!isStaff && mine && (
                    <Box
                      as="button"
                      onClick={() => handleDelete(n)}
                      {...({ disabled: busyId === n.id } as any)}
                      sx={{
                        background: "transparent",
                        border: "none",
                        padding: 0,
                        fontFamily: "Poppins",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: mnt.textMuted,
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Delete
                    </Box>
                  )}
                </Flex>

                <Box
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "13px",
                    color: mnt.ink,
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.5,
                  }}
                >
                  {n.note}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
